import { Camera, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageCropper } from '@/components/shared/image-cropper';
import { MemberPhotoUploader } from '@/components/shared/member-photo-uploader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-errors';
import { uploadBlob, type UploadPurpose } from '@/lib/upload';
import { cn } from '@/utils/cn';

import type { SacramentPersonDetails } from './sacrament-people-fields';

const revokeBlob = (url: string | null) => {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
};

export const SacramentImagesFields = ({
  personName,
  value,
  onChange,
}: {
  personName: string;
  value: Pick<
    SacramentPersonDetails,
    'photoKey' | 'photoPreviewUrl' | 'certificatePhotoKey' | 'certificatePhotoPreviewUrl'
  >;
  onChange: (
    patch: Partial<
      Pick<
        SacramentPersonDetails,
        'photoKey' | 'photoPreviewUrl' | 'certificatePhotoKey' | 'certificatePhotoPreviewUrl'
      >
    >,
  ) => void;
}) => {
  const { t } = useTranslation('sacraments');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const displayName = personName.trim() || t('people.extraDetailsUnnamed');

  const upload = async (purpose: UploadPurpose, blob: Blob, kind: 'person' | 'certificate') => {
    try {
      const { key, url } = await uploadBlob({ purpose, blob });
      const preview = url ?? URL.createObjectURL(blob);
      if (kind === 'person') {
        if (preview !== value.photoPreviewUrl) revokeBlob(value.photoPreviewUrl);
        onChange({ photoKey: key, photoPreviewUrl: preview });
        return;
      }
      if (preview !== value.certificatePhotoPreviewUrl) revokeBlob(value.certificatePhotoPreviewUrl);
      onChange({ certificatePhotoKey: key, certificatePhotoPreviewUrl: preview });
    } catch (error) {
      toast.error(
        error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'),
      );
      throw error;
    }
  };

  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
      <div className="w-[7.5rem] shrink-0 self-center sm:self-start">
        <p className="mb-1.5 text-center text-caption text-fg-secondary">{t('people.photo')}</p>
        <MemberPhotoUploader
          src={value.photoPreviewUrl}
          name={displayName}
          size="xl"
          className="flex-col items-center"
          onUpload={(blob) => upload('member_photo', blob, 'person')}
          onRemove={() => {
            revokeBlob(value.photoPreviewUrl);
            onChange({ photoKey: null, photoPreviewUrl: null });
          }}
          labels={{
            change: t('people.photoChange'),
            remove: t('people.photoRemove'),
            cropTitle: t('people.photoCropTitle'),
            zoom: tCommon('media.zoom'),
            apply: t('people.photoApply'),
            cancel: tCommon('action.cancel'),
            instructions: t('people.photoInstructions'),
            closeCropper: tCommon('action.close'),
          }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="mb-1.5 text-caption text-fg-secondary">{t('people.certificatePhoto')}</p>
        <CertificatePhotoUploader
          src={value.certificatePhotoPreviewUrl}
          name={displayName}
          onUpload={(blob) => upload('register_certificate', blob, 'certificate')}
          onRemove={() => {
            revokeBlob(value.certificatePhotoPreviewUrl);
            onChange({ certificatePhotoKey: null, certificatePhotoPreviewUrl: null });
          }}
          labels={{
            change: t('people.certificatePhotoChange'),
            remove: t('people.certificatePhotoRemove'),
            cropTitle: t('people.certificatePhotoCropTitle'),
            zoom: tCommon('media.zoom'),
            apply: t('people.photoApply'),
            cancel: tCommon('action.cancel'),
            instructions: t('people.certificatePhotoInstructions'),
            closeCropper: tCommon('action.close'),
          }}
        />
      </div>
    </div>
  );
};

const CertificatePhotoUploader = ({
  src,
  name,
  onUpload,
  onRemove,
  labels,
}: {
  src?: string | null;
  name: string;
  onUpload: (blob: Blob) => Promise<void> | void;
  onRemove?: () => void;
  labels: {
    change: string;
    remove: string;
    cropTitle: string;
    zoom: string;
    apply: string;
    cancel: string;
    instructions: string;
    closeCropper?: string;
  };
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [candidate, setCandidate] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(
    () => () => {
      if (candidate) URL.revokeObjectURL(candidate);
    },
    [candidate],
  );

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          'relative aspect-[3/2] w-full max-w-sm overflow-hidden rounded-sm border border-border bg-surface-sunken',
        )}
      >
        {src ? (
          <img src={src} alt={name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-caption text-fg-tertiary">
            {labels.change}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          leadingIcon={<Camera className="h-4 w-4" />}
        >
          {labels.change}
        </Button>
        {src && onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            leadingIcon={<Trash2 className="h-4 w-4" />}
            className="text-danger-fg"
          >
            {labels.remove}
          </Button>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) setCandidate(URL.createObjectURL(file));
          event.target.value = '';
        }}
      />
      <Dialog open={candidate !== null} onOpenChange={(open) => !open && setCandidate(null)}>
        <DialogContent size="md" closeLabel={labels.closeCropper}>
          <DialogHeader>
            <DialogTitle>{labels.cropTitle}</DialogTitle>
          </DialogHeader>
          {candidate ? (
            <ImageCropper
              src={candidate}
              aspect={3 / 2}
              circular={false}
              maxSize={1600}
              applying={uploading}
              onCancel={() => setCandidate(null)}
              onCropped={async (blob) => {
                setUploading(true);
                try {
                  await onUpload(blob);
                  setCandidate(null);
                } finally {
                  setUploading(false);
                }
              }}
              labels={{
                zoom: labels.zoom,
                apply: labels.apply,
                cancel: labels.cancel,
                instructions: labels.instructions,
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
